import React, { useState } from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material'
import { validatePatientInfo } from 'src/supportPortal/services/patientProspectsApi'
import { toast } from 'react-toastify'

/**
 * Mark a {@link PatientInfo} as identity-validated. Used after a
 * prospect-promotion to clear the {@code requiresValidation=true} flag
 * once a clinic / support staff member has confirmed the patient's
 * identifying details.
 *
 * Reusable on any patient row that carries {@code requiresValidation: true}
 * — pass the patientInfo object as the `patientInfo` prop. The component
 * is intentionally standalone so the existing PatientList in
 * {@code views/patients/} can drop it in without other plumbing.
 */
export default function ValidatePatientInfoDialog({ open, patientInfo, onClose, onValidated }) {
  const [name, setName] = useState(true)
  const [mobile, setMobile] = useState(true)
  const [dob, setDob] = useState(false)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    setError('')
    if (!patientInfo?.id) {
      setError('Missing patientInfoId.')
      return
    }
    const verifiedFields = []
    if (name) verifiedFields.push('name')
    if (mobile) verifiedFields.push('mobile')
    if (dob) verifiedFields.push('dob')
    setBusy(true)
    try {
      const res = await validatePatientInfo(patientInfo.id, {
        notes: notes.trim() || null,
        verifiedFields,
      })
      if (res?.status >= 200 && res?.status < 300) {
        toast.success('Patient validated.')
        onValidated?.(res.data)
        onClose?.()
      } else {
        const msg = res?.response?.data?.message || res?.data?.message || 'Validation failed.'
        setError(msg)
      }
    } catch (e) {
      setError(e?.message || 'Validation failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Validate patient identity</DialogTitle>
      <DialogContent dividers>
        <div style={{ marginBottom: 12, fontSize: 14, color: '#475569' }}>
          {patientInfo?.name ? <><strong>{patientInfo.name}</strong> &middot; </> : null}
          {patientInfo?.mobile}
        </div>
        <div style={{ display: 'grid', gap: 4, marginBottom: 8 }}>
          <FormControlLabel
            control={<Checkbox checked={name} onChange={(e) => setName(e.target.checked)} />}
            label="Verified name"
          />
          <FormControlLabel
            control={<Checkbox checked={mobile} onChange={(e) => setMobile(e.target.checked)} />}
            label="Verified mobile number"
          />
          <FormControlLabel
            control={<Checkbox checked={dob} onChange={(e) => setDob(e.target.checked)} />}
            label="Verified date of birth"
          />
        </div>
        <TextField
          label="Notes"
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          margin="dense"
          multiline
          minRows={2}
          helperText="Audit-only. e.g. 'Confirmed verbally during pre-call.'"
        />
        {error ? (
          <div style={{ color: '#b42318', marginTop: 8, fontSize: 13 }}>{error}</div>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={busy}>
          {busy ? 'Saving…' : 'Mark validated'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
